import { useState } from "react";
import { useTranslation } from "react-i18next";
import { COMMON_TIMEZONES, DENSITIES, type Density } from "@tc/shared";
import { Card, Segmented, Select, Toast } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useLanguage } from "@/lib/i18n/language";
import { useDensity } from "@/lib/prefs/density";
import { updatePreferences } from "../api";

/**
 * Settings → Preferences (TW-3466). Language (reuses LanguageProvider + PATCH /api/me/language),
 * timezone (PATCH /api/me/preferences → user.timezone, the formatting zone for dates across the
 * UI) and interface density (regular/compact, drives `.dense` app-wide). Each control saves
 * instantly; the row vocabulary is the shared `.set-row`.
 */
export function PreferencesSection() {
  const { t } = useTranslation();
  const { lang, setLang } = useLanguage();
  const { density, setDensity } = useDensity();
  const { me, refresh } = useAuthz();
  const [toast, setToast] = useState<string | null>(null);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  async function changeTimezone(value: string) {
    const timezone = value === "" ? null : value;
    try {
      await updatePreferences({ timezone });
      await refresh(); // re-pull /api/me so date formatting across the app adopts the new zone
      flash(t("settings.preferences.saved"));
    } catch {
      flash(t("settings.preferences.saveError"));
    }
  }

  const densityOptions = DENSITIES.map((d: Density) => ({
    value: d,
    label: t(`settings.preferences.density.${d}`),
  }));

  return (
    <div>
      <div className="set-section-head" style={{ marginBottom: 18 }}>
        <h2>{t("settings.preferences.heading")}</h2>
        <div className="sub">{t("settings.preferences.sub")}</div>
      </div>

      <Card>
        <div className="set-row">
          <div className="sr-text">
            <div className="sr-label">{t("settings.preferences.language.label")}</div>
            <div className="sr-sub">{t("settings.preferences.language.sub")}</div>
          </div>
          <Segmented
            size="sm"
            value={lang}
            onChange={(v) => setLang(v as "en" | "es")}
            options={[
              { value: "en", label: "English" },
              { value: "es", label: "Español" },
            ]}
          />
        </div>

        <div className="set-row">
          <div className="sr-text">
            <div className="sr-label">{t("settings.preferences.timezone.label")}</div>
            <div className="sr-sub">{t("settings.preferences.timezone.sub")}</div>
          </div>
          <Select
            value={me?.timezone ?? ""}
            onChange={(v) => void changeTimezone(v)}
            options={[
              { value: "", label: t("settings.preferences.timezone.auto") },
              ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz.replace(/_/g, " ") })),
            ]}
          />
        </div>

        <div className="set-row">
          <div className="sr-text">
            <div className="sr-label">{t("settings.preferences.density.label")}</div>
            <div className="sr-sub">{t("settings.preferences.density.sub")}</div>
          </div>
          <Segmented size="sm" value={density} onChange={setDensity} options={densityOptions} />
        </div>
      </Card>

      {toast && <Toast message={toast} />}
    </div>
  );
}
